import type { FormProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Button,
  Center,
  Flex,
  Form,
  Heading,
  Icon,
  Label,
  Sheet,
  VisuallyHiddenInput,
  system,
  useToast,
} from '@hover/blueprint';
import { iCheck, iExternalLink, iPlus, iconUpload } from '@hover/icons';
import React, { type BaseSyntheticEvent, useRef } from 'react';
import { Trans } from 'react-i18next';

import { embedFooterStyles } from 'src/modules/homeownerApp/components/Embed/components/EmbedFooter';
import type { LeadFormData } from 'src/modules/pro/components/LeadForm';
import { LeadForm } from 'src/modules/pro/components/LeadForm';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { useEmbedContext } from 'src/shared/providers/Embed';
import type { CreateLeadResponse } from 'src/shared/requests/createLead';
import { createLead } from 'src/shared/requests/createLead';
import { Route } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { logErrorWithTags } from 'src/shared/telemetry/logger';
import { StatusError } from 'src/shared/utils/request';

import {
  getUserSession,
  useHomeownerAppContext,
  useHomeownerTranslate,
} from '../hooks';

type LeadSheetProps = {
  onClose: () => void;
  isOpen: boolean;
  redirectTo?: string;
};

const SubmittedState = ({ ...props }: FormProps) => {
  const t = useHomeownerTranslate();
  const { leadData } = useEmbedContext();

  return (
    <Form
      flexDirection={{ base: 'column', tablet: 'row' }}
      justifyContent={{ base: 'center', tablet: 'space-between' }}
      width={1}
      {...props}
    >
      <VisuallyHiddenInput name="leadId" value={leadData?.id} />
      <VisuallyHiddenInput name="fullName" value={leadData?.full_name} />
      <VisuallyHiddenInput name="email" value={leadData?.email} />
      <Flex flex="1 1 50%" flexDir="column" justifyContent="center">
        <Center bg="neutral.50" borderRadius="50%" h={350} mb={500} w={350}>
          <Icon icon={iCheck} size="large" />
        </Center>
        <Heading size={{ base: 'medium', tablet: 'large' }}>
          {t('leadSheet.gotIt')}
        </Heading>
        <Body color="text.secondary" maxW={750} size="small">
          {t('leadSheet.weWillReachOutSoon')}
        </Body>
      </Flex>
      <Flex flex="1 1 50%">
        <Box
          alignItems="center"
          as="button"
          backgroundColor="transparent"
          borderColor="interactive.stroke"
          borderRadius={300}
          borderStyle="solid"
          borderWidth={400}
          cursor="pointer"
          display={{ base: 'none', tablet: 'flex' }}
          flex={1}
          flexDirection="column"
          height="364px"
          justifyContent="center"
          type="submit"
        >
          <>
            <Icon icon={iPlus} size="small" />
            <Label
              cursor="pointer"
              fontWeight={700}
              textAlign="center"
              width="100%"
            >
              <Trans
                components={{
                  LineBreak: <system.br display="initial" />,
                }}
                i18nKey="leadSheet.uploadAPhoto"
                t={t}
              />
            </Label>
          </>
        </Box>
        <Button
          display={{ base: 'flex', tablet: 'none' }}
          iconAfter={iconUpload}
          size="large"
          type="submit"
          width={1}
        >
          {t('embed.addYourPhoto')}
        </Button>
      </Flex>
    </Form>
  );
};

export const LeadSheet = ({ isOpen, onClose, redirectTo }: LeadSheetProps) => {
  const {
    leadData,
    setLeadData,
    leadFormHeader,
    leadFormSubheader,
    sendHomeownerEmail,
  } = useEmbedContext();
  const { trackingData } = useHomeownerAppContext();
  const hasSubmittedLead = !!leadData;
  const t = useHomeownerTranslate();
  const toast = useToast();

  const { sessionId } = getUserSession() || {};

  const publicIdentifier = useGlobalStorageContext().affOrgPublicId;
  const url = new URL(
    `${Route.SIGN_UP_ONBOARDING.path}/homeowner`,
    window.location.origin,
  );
  if (publicIdentifier) {
    url.searchParams.set('aff_org_id', publicIdentifier);
  }

  if (redirectTo) {
    url.searchParams.set('redirect_to', redirectTo);
  }

  if (sessionId) {
    url.searchParams.set('session_id', sessionId);
  }

  // We need the form to post to this route, that way we can pass the post data
  // to the route once it renders. This is because we don't want to pass query
  // params directly to the route for logging and other PII concerns.
  const leadSheetFormProps = {
    action: url.toString(),
    method: 'POST',
    target: '_blank',
  };
  const leadInputRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (
    formData: LeadFormData,
    event: BaseSyntheticEvent | undefined,
  ) => {
    buttonPressed({
      ...trackingData,
      button_text: 'Continue',
      track_id: 'embed-lead-form-submit',
    });

    if (!publicIdentifier || !event) return;

    event.preventDefault();
    let result: CreateLeadResponse | undefined;
    let leadId: number | undefined;

    try {
      result = await createLead(
        publicIdentifier,
        formData.email || '',
        formData.phoneNumber || '',
        formData.postalCode || '',
        formData.fullName || '',
        sendHomeownerEmail,
      );
      leadId = result?.id;
    } catch (error) {
      const errorStatusCode =
        error instanceof StatusError ? error.response.status : 500;

      if (errorStatusCode !== 429) {
        logErrorWithTags(error as Error, {
          featureName: 'DesignerEmbedLeadSheet',
        });
      }

      toast({
        description: t('leadSheet.theresAnIssue'),
        position: 'top',
        props: { colorScheme: 'dark' },
        status: 'error',
      });
    } finally {
      if (result && leadId) {
        setLeadData(result);
        const hiddenInput = leadInputRef.current;
        if (hiddenInput) {
          hiddenInput.value = leadId.toString();
        }
      }

      const target = event?.target as HTMLFormElement;

      target?.submit();
    }
  };

  const Footer = (
    <Flex
      // Disables the box shadow on all sides except the top
      clipPath={{
        base: 'inset(-48px 0px 0px 0px)',
        tablet: 'inset(-15px 0px 0px 0px)',
      }}
      justifyContent="center"
      mt={0}
      pt={{ base: 400, tablet: 600 }}
      px={400}
      sx={embedFooterStyles}
    >
      <Button
        form="embed-lead-form"
        iconAfter={iExternalLink}
        size="large"
        type="submit"
        width={{ base: 1, tablet: 'auto' }}
      >
        {t('leadSheet.continue')}
      </Button>
    </Flex>
  );

  return (
    <Sheet
      autoFocus={false}
      blockScrollOnMount={false}
      contentProps={{
        pt: { base: 800, tablet: 1000 },
        pl: {
          base: 0,
          tablet: 500,
        },
        pr: {
          base: 0,
          tablet: 500,
        },
      }}
      footer={hasSubmittedLead ? undefined : Footer}
      isOpen={isOpen}
      onClose={onClose}
      variant="bottom"
    >
      <Flex height="100%" justifyContent="center" mb={600} px={400}>
        <Flex flexDir={{ base: 'column', tablet: 'row' }} maxW="1440px" w={1}>
          {hasSubmittedLead ? (
            <SubmittedState {...leadSheetFormProps} />
          ) : (
            <>
              <Flex flex="1 1 50%" flexDir="column" justifyContent="center">
                <Heading size="large">
                  {leadFormHeader || t('leadSheet.readyToMakeItReal')}
                </Heading>
                <Body color="text.secondary" maxW={750} size="small">
                  {leadFormSubheader || t('leadSheet.createAnAccountToSave')}
                </Body>
              </Flex>
              <Flex flex="1 1 50%">
                <LeadForm
                  externalSubmitButtonFormId="embed-lead-form"
                  hideInternalFormButton
                  leadInputRef={leadInputRef}
                  onSubmit={onSubmit}
                  {...leadSheetFormProps}
                  trackingData={{
                    ...trackingData,
                    track_id: 'embed-lead-form-start',
                    button_location: 'embed-lead-sheet',
                  }}
                />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </Sheet>
  );
};
